import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financialsApi } from '@/lib/financialsApi';
import { toNumber } from '@/lib/number';
import { showSuccess, showError } from '@/lib/apiErrors';
import { useMembership } from '@/hooks/useMembership';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Filter, Pencil, Trash2, Receipt, ChevronDown } from 'lucide-react';
import type { ApiListResponse } from '@/types/api';
import type { Expense } from '@/types/financials';

interface ExpenseListProps {
  farmId: string;
  /** Optional: filter to a specific cycle */
  cycleId?: string;
  onEdit?: (expense: Expense) => void;
  onMutationSuccess?: () => void;
}

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
}

interface Cycle {
  id: string;
  pond_name: string;
  cycle_number: number;
  status: string;
}

export function ExpenseList({
  farmId,
  cycleId,
  onEdit,
  onMutationSuccess,
}: ExpenseListProps) {
  const { membership, isSupervisorOrAbove } = useMembership();
  const queryClient = useQueryClient();

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [cycleFilter, setCycleFilter] = useState(cycleId || 'all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  // Build params
  const params = useMemo(() => {
    const p: Record<string, string> = { limit: '50' };
    if (categoryFilter !== 'all') p.category = categoryFilter;
    if (cycleFilter !== 'all') p.cycle_id = cycleFilter;
    if (dateFrom) p.start_date = dateFrom;
    if (dateTo) p.end_date = dateTo;
    if (cursor) p.cursor = cursor;
    return p;
  }, [categoryFilter, cycleFilter, dateFrom, dateTo, cursor]);

  // Fetch expense categories
  const { data: categoriesData } = useQuery({
    queryKey: ['financials', 'categories'],
    queryFn: () => financialsApi.listCategories(),
    staleTime: 5 * 60 * 1000,
  });
  const categories = categoriesData?.data ?? [];

  // Fetch cycles for filter dropdown
  const { data: cyclesData } = useQuery({
    queryKey: ['cycles', membership?.farmId],
    queryFn: async () => {
      const response = await api.get<{ data: Cycle[] }>(
        `/api/v1/farms/${membership?.farmId}/cycles`
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId && !cycleId,
  });
  const cycles = cyclesData || [];

  // Fetch expenses
  const { data, isLoading, error, refetch } = useQuery<ApiListResponse<Expense>>({
    queryKey: ['financials', 'expenses', farmId, params],
    queryFn: () => financialsApi.listExpenses(farmId, params),
    staleTime: 30 * 1000,
  });

  const expenses = data?.data ?? [];
  const meta = data?.meta;
  const hasMore = meta?.has_more === true;
  const nextCursor = meta?.cursor ?? undefined;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (expenseId: string) => financialsApi.deleteExpense(farmId, expenseId),
    onSuccess: () => {
      showSuccess('Expense deleted');
      queryClient.invalidateQueries({ queryKey: ['financials'] });
      onMutationSuccess?.();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to delete expense');
    },
  });

  function handleDelete(expenseId: string) {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      deleteMutation.mutate(expenseId);
    }
  }

  function handleLoadMore() {
    if (nextCursor) {
      setCursor(nextCursor);
    }
  }

  function clearFilters() {
    setCategoryFilter('all');
    setCycleFilter(cycleId || 'all');
    setDateFrom('');
    setDateTo('');
    setCursor(undefined);
  }

  if (isLoading && !cursor) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-4">
        <LoadingSpinner size={24} className="text-primary-600" />
        <p className="text-sm text-gray-500">Loading expenses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load expenses'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
        </div>
        <Select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setCursor(undefined);
          }}
          options={[
            { value: 'all', label: 'All Categories' },
            ...categories.map((c) => ({ value: c.code, label: c.name })),
          ]}
        />
        {!cycleId && (
          <Select
            value={cycleFilter}
            onChange={(e) => {
              setCycleFilter(e.target.value);
              setCursor(undefined);
            }}
            options={[
              { value: 'all', label: 'All Cycles' },
              ...cycles.map((c) => ({
                value: c.id,
                label: `${c.pond_name} #${c.cycle_number}`,
              })),
            ]}
          />
        )}
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setCursor(undefined);
          }}
          placeholder="From"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setCursor(undefined);
          }}
          placeholder="To"
        />
        {(categoryFilter !== 'all' || cycleFilter !== (cycleId || 'all') || dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        )}
      </div>

      {/* Expense table */}
      {expenses.length === 0 ? (
        <Card>
          <Card.Body>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Receipt className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="font-semibold text-gray-900">No expenses found</h3>
              <p className="text-sm text-gray-500 mt-1">
                {categoryFilter !== 'all' || dateFrom || dateTo
                  ? 'Try adjusting your filters.'
                  : 'Add your first expense to start tracking costs.'}
              </p>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-gray-500">
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium">Category</th>
                  <th className="px-5 py-3 font-medium">Description</th>
                  <th className="px-5 py-3 font-medium">Allocation</th>
                  <th className="px-5 py-3 font-medium text-right">Amount</th>
                  <th className="px-5 py-3 font-medium">Vendor</th>
                  <th className="px-5 py-3 font-medium">By</th>
                  {isSupervisorOrAbove && (
                    <th className="px-5 py-3 font-medium text-right">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {expenses.map((expense) => {
                  const amt = toNumber(expense.amount);
                  return (
                    <tr key={expense.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 text-gray-900 whitespace-nowrap">
                        {new Date(expense.expense_date).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-gray-700">
                          {expense.category_name || expense.category}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-600 max-w-xs truncate">
                        {expense.description || '--'}
                      </td>
                      <td className="px-5 py-3">
                        <AllocationBadge
                          type={expense.allocation_type}
                          cycleName={expense.cycle_name}
                        />
                      </td>
                      <td className="px-5 py-3 text-right font-medium text-gray-900">
                        {formatPHP(amt)}
                      </td>
                      <td className="px-5 py-3 text-gray-500">
                        {expense.vendor || '--'}
                      </td>
                      <td className="px-5 py-3 text-gray-500">
                        {expense.created_by_name || '--'}
                      </td>
                      {isSupervisorOrAbove && (
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {onEdit && (
                              <button
                                onClick={() => onEdit(expense)}
                                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                title="Edit expense"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(expense.id)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete expense"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="px-5 py-3 border-t border-gray-100 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ChevronDown className="h-4 w-4" />}
                onClick={handleLoadMore}
                loading={isLoading && !!cursor}
              >
                Load more
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function AllocationBadge({
  type,
  cycleName,
}: {
  type: string;
  cycleName: string | null;
}) {
  switch (type) {
    case 'cycle':
      return (
        <Badge variant="ok" size="sm">
          {cycleName || 'Cycle'}
        </Badge>
      );
    case 'pond':
      return <Badge variant="neutral" size="sm">Pond</Badge>;
    case 'farm':
      return <Badge variant="warning" size="sm">Farm-wide</Badge>;
    default:
      return <Badge variant="neutral" size="sm">{type}</Badge>;
  }
}
